// src/components/DialogDescontoAcrescimo.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Paper, Typography, Divider, Stack, Box, IconButton, useTheme, Zoom, alpha
} from '@mui/material';
import { Percent as PercentIcon, Close as CloseIcon, RemoveCircle as RemoveCircleIcon, AddCircle as AddCircleIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { VFieldPercentFaturamento } from './VFieldPercentFaturamento';
import { VFieldMoney2FaturamentoPercentual } from './VFieldMoney2FaturamentoPercentual';
import { PDVThemeColors } from './PDVThemes';

// Cor fixa para "desconto" (não usa vermelho, que já é reservado para erros)
const DESCONTO_COLOR = '#0D9488';

interface DialogDescontoAcrescimoProps {
  open: boolean;
  onClose: () => void;
  totalItens: number;
  desconto: number;
  acrescimo: number;
  onDescontoChange: (valor: number) => void;
  onAcrescimoChange: (valor: number) => void;
  formatarMoeda: (valor: number) => string;
  colors: PDVThemeColors;
}

export const DialogDescontoAcrescimo: React.FC<DialogDescontoAcrescimoProps> = ({
  open,
  onClose,
  totalItens,
  desconto,
  acrescimo,
  onDescontoChange,
  onAcrescimoChange,
  formatarMoeda,
  colors,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [descontoPercentual, setDescontoPercentual] = useState<number>(0);
  const [acrescimoPercentual, setAcrescimoPercentual] = useState<number>(0);
  const [descontoLocal, setDescontoLocal] = useState<number>(desconto);
  const [acrescimoLocal, setAcrescimoLocal] = useState<number>(acrescimo);

  const refDescontoValor = useRef<HTMLInputElement>(null);
  const refDescontoPercentual = useRef<HTMLInputElement>(null);
  const refAcrescimoValor = useRef<HTMLInputElement>(null);
  const refAcrescimoPercentual = useRef<HTMLInputElement>(null);
  const refConfirmar = useRef<HTMLButtonElement>(null);

  // Sincroniza os valores e foca o campo de desconto apenas quando o dialog abre.
  // Não pode depender de `desconto`/`acrescimo`: como eles sobem para o pai a cada
  // tecla digitada e voltam como prop, incluí-los aqui fazia o efeito rodar de novo
  // a cada dígito e roubar o foco de volta para o campo de desconto.
  useEffect(() => {
    if (open) {
      setDescontoLocal(desconto);
      setAcrescimoLocal(acrescimo);

      const novoPercentualDesconto = totalItens > 0
        ? Number(((desconto / totalItens) * 100).toFixed(2))
        : 0;

      const novoPercentualAcrescimo = totalItens > 0
        ? Number(((acrescimo / totalItens) * 100).toFixed(2))
        : 0;

      setDescontoPercentual(novoPercentualDesconto);
      setAcrescimoPercentual(novoPercentualAcrescimo);

      // sempre que o dialog abrir, o foco vai direto para o campo de desconto
      setTimeout(() => {
        refDescontoValor.current?.focus();
      }, 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const moverFoco = (ref: React.RefObject<HTMLInputElement | HTMLButtonElement>) => {
    ref.current?.focus();
  };

  const aoPressionarEnter = (proximo: React.RefObject<HTMLInputElement | HTMLButtonElement>) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      moverFoco(proximo);
    }
  };

  // Handler para mudança no valor do desconto
  const handleDescontoValor = (_id: string, valor: string) => {
    const valorNum = Number(valor) || 0;
    setDescontoLocal(valorNum);
    onDescontoChange(valorNum);

    const percentual = totalItens > 0
      ? Number(((valorNum / totalItens) * 100).toFixed(2))
      : 0;
    setDescontoPercentual(percentual);
  };

  // Handler para mudança no percentual do desconto
  const handleDescontoPercentual = (_id: string, valor: string | number) => {
    const percentual = typeof valor === 'string' ? parseFloat(valor) || 0 : valor || 0;
    const percentualLimitado = Math.min(Math.max(percentual, 0), 100);
    const valorCalculado = (totalItens * percentualLimitado) / 100;

    setDescontoPercentual(percentualLimitado);
    setDescontoLocal(valorCalculado);
    onDescontoChange(valorCalculado);
  };

  // Handler para mudança no valor do acréscimo
  const handleAcrescimoValor = (_id: string, valor: string) => {
    const valorNum = Number(valor) || 0;
    setAcrescimoLocal(valorNum);
    onAcrescimoChange(valorNum);

    const percentual = totalItens > 0
      ? Number(((valorNum / totalItens) * 100).toFixed(2))
      : 0;
    setAcrescimoPercentual(percentual);
  };

  // Handler para mudança no percentual do acréscimo
  const handleAcrescimoPercentual = (_id: string, valor: string | number) => {
    const percentual = typeof valor === 'string' ? parseFloat(valor) || 0 : valor || 0;
    const percentualLimitado = Math.min(Math.max(percentual, 0), 1000);
    const valorCalculado = (totalItens * percentualLimitado) / 100;

    setAcrescimoPercentual(percentualLimitado);
    setAcrescimoLocal(valorCalculado);
    onAcrescimoChange(valorCalculado);
  };

  const confirmarEFechar = () => {
    onClose();
  };

  const valorDesconto = descontoLocal;
  const valorAcrescimo = acrescimoLocal;
  const totalFinal = totalItens + valorAcrescimo - valorDesconto;

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `0.5px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              background: colors.primaryGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PercentIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Desconto e acréscimo
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          {/* Desconto */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: '12px',
              borderColor: isDark ? alpha(DESCONTO_COLOR, 0.35) : alpha(DESCONTO_COLOR, 0.25),
              bgcolor: isDark ? alpha(DESCONTO_COLOR, 0.1) : alpha(DESCONTO_COLOR, 0.05),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <RemoveCircleIcon sx={{ fontSize: 16, color: DESCONTO_COLOR }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: DESCONTO_COLOR }}>
                Desconto
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 2 }}>
                <VFieldMoney2FaturamentoPercentual
                  id="desconto"
                  label="Valor (R$)"
                  value={descontoLocal}
                  valor={handleDescontoValor}
                  inputRef={refDescontoValor}
                  onKeyDown={aoPressionarEnter(refDescontoPercentual)}
                  size="small"
                  fullWidth
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <VFieldPercentFaturamento
                  id="descontoPercentual"
                  label="%"
                  value={descontoPercentual}
                  max={100}
                  valor={handleDescontoPercentual}
                  inputRef={refDescontoPercentual}
                  onKeyDown={aoPressionarEnter(refAcrescimoValor)}
                  size="small"
                  fullWidth
                />
              </Box>
            </Stack>
          </Paper>

          {/* Acréscimo */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: '12px',
              borderColor: isDark ? 'rgba(46,125,50,0.3)' : 'rgba(46,125,50,0.2)',
              bgcolor: isDark ? 'rgba(46,125,50,0.07)' : 'rgba(46,125,50,0.035)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <AddCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="subtitle2" fontWeight={600} color="success.main">
                Acréscimo
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 2 }}>
                <VFieldMoney2FaturamentoPercentual
                  id="acrescimo"
                  label="Valor (R$)"
                  value={acrescimoLocal}
                  valor={handleAcrescimoValor}
                  inputRef={refAcrescimoValor}
                  onKeyDown={aoPressionarEnter(refAcrescimoPercentual)}
                  size="small"
                  fullWidth
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <VFieldPercentFaturamento
                  id="acrescimoPercentual"
                  label="%"
                  value={acrescimoPercentual}
                  max={1000}
                  valor={handleAcrescimoPercentual}
                  inputRef={refAcrescimoPercentual}
                  onKeyDown={aoPressionarEnter(refConfirmar)}
                  size="small"
                  fullWidth
                />
              </Box>
            </Stack>
          </Paper>

          {/* Resumo */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: '12px',
              borderColor,
              bgcolor: isDark ? alpha(colors.primarySolid, 0.14) : alpha(colors.primarySolid, 0.06),
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" fontWeight={500}>{formatarMoeda(totalItens)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
              <Typography variant="body2" color="text.secondary">Desconto</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ color: DESCONTO_COLOR }}>
                {valorDesconto > 0 ? `– ${formatarMoeda(valorDesconto)}` : formatarMoeda(0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
              <Typography variant="body2" color="text.secondary">Acréscimo</Typography>
              <Typography variant="body2" fontWeight={500} color="success.main">
                {valorAcrescimo > 0 ? `+ ${formatarMoeda(valorAcrescimo)}` : formatarMoeda(0)}
              </Typography>
            </Box>

            <Divider sx={{ my: 0.75, borderColor }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Total</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                {formatarMoeda(totalFinal)}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button
          ref={refConfirmar}
          variant="contained"
          fullWidth
          onClick={confirmarEFechar}
          startIcon={<CheckCircleIcon />}
          sx={{
            borderRadius: '10px',
            py: 1,
            background: colors.primaryGradient,
            color: colors.textOnPrimary,
            '&:hover': { background: colors.hoverGradient },
          }}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
